import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { StaticRouter } from "react-router-dom/server";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/contexts/LanguageContext";
import HomePage from "./pages/HomePage";
import GrassPage from "./pages/GrassPage";
import SoilPage from "./pages/SoilPage";
import RockPage from "./pages/RockPage";
import MulchPage from "./pages/MulchPage";
import ContactPage from "./pages/ContactPage";
import SandPage from "./pages/SandPage";
import QuotePage from "./pages/QuotePage";
import ProductsPage from "./pages/ProductsPage";
import ServicesPage from "./pages/ServicesPage";
import SodDeliveryPage from "./pages/SodDeliveryPage";
import BulkDeliveryPage from "./pages/BulkDeliveryPage";
import AreasPage from "./pages/AreasPage";
import AreaDetailPage from "./pages/AreaDetailPage";
import AboutPage from "./pages/AboutPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import NotFound from "./pages/NotFound";
import AuthPage from "./pages/AuthPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import ScrollToTop from "./components/ScrollToTop";

const queryClient = new QueryClient();

interface AppProps {
  /** When provided the app renders with a StaticRouter (used by the build-time prerender). */
  url?: string;
  helmetContext?: Record<string, unknown>;
}

const App = ({ url, helmetContext }: AppProps) => {
  const Router = url
    ? ({ children }: { children: React.ReactNode }) => (
        <StaticRouter location={url}>{children}</StaticRouter>
      )
    : BrowserRouter;

  return (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider context={helmetContext}>
      <TooltipProvider>
        <LanguageProvider initialLanguage={url?.startsWith('/es') ? 'es' : 'en'}>
          <Toaster />
          <Sonner />
          <Router>
            <ScrollToTop />
          <Routes>
            {/* Redirect root to English */}
            <Route path="/" element={<Navigate to="/en" replace />} />
            
            {/* English Routes */}
            <Route path="/en" element={<HomePage />} />
            <Route path="/en/products" element={<ProductsPage />} />
            <Route path="/en/grass" element={<GrassPage />} />
            <Route path="/en/grass/:product" element={<GrassPage />} />
            <Route path="/en/rock" element={<RockPage />} />
            <Route path="/en/mulch" element={<MulchPage />} />
            <Route path="/en/soil" element={<SoilPage />} />
            <Route path="/en/sand" element={<SandPage />} />
            <Route path="/en/services" element={<ServicesPage />} />
            <Route path="/en/services/sod-delivery" element={<SodDeliveryPage />} />
            <Route path="/en/services/bulk-delivery" element={<BulkDeliveryPage />} />
            <Route path="/en/areas" element={<AreasPage />} />
            <Route path="/en/areas/:areaSlug" element={<AreaDetailPage />} />
            <Route path="/en/blog" element={<BlogPage />} />
            <Route path="/en/blog/:postSlug" element={<BlogPostPage />} />
            <Route path="/en/about" element={<AboutPage />} />
            <Route path="/en/contact" element={<ContactPage />} />
            <Route path="/en/quote" element={<QuotePage />} />
            <Route path="/en/auth" element={<AuthPage />} />
            <Route path="/en/privacy" element={<PrivacyPage />} />
            <Route path="/en/terms" element={<TermsPage />} />
            
            {/* Spanish Routes */}
            <Route path="/es" element={<HomePage />} />
            <Route path="/es/productos" element={<ProductsPage />} />
            <Route path="/es/pasto" element={<GrassPage />} />
            <Route path="/es/pasto/:product" element={<GrassPage />} />
            <Route path="/es/roca" element={<RockPage />} />
            <Route path="/es/mulch" element={<MulchPage />} />
            <Route path="/es/tierra" element={<SoilPage />} />
            <Route path="/es/arena" element={<SandPage />} />
            <Route path="/es/servicios" element={<ServicesPage />} />
            <Route path="/es/servicios/entrega-cesped" element={<SodDeliveryPage />} />
            <Route path="/es/servicios/entrega-granel" element={<BulkDeliveryPage />} />
            <Route path="/es/areas" element={<AreasPage />} />
            <Route path="/es/areas/:areaSlug" element={<AreaDetailPage />} />
            <Route path="/es/blog" element={<BlogPage />} />
            <Route path="/es/blog/:postSlug" element={<BlogPostPage />} />
            <Route path="/es/nosotros" element={<AboutPage />} />
            <Route path="/es/contacto" element={<ContactPage />} />
            <Route path="/es/cotizar" element={<QuotePage />} />
            <Route path="/es/auth" element={<AuthPage />} />
            <Route path="/es/privacidad" element={<PrivacyPage />} />
            <Route path="/es/terminos" element={<TermsPage />} />

            {/* Legacy slug redirects (301-equivalent SPA redirects) */}
            <Route path="/es/mantillo" element={<Navigate to="/es/mulch" replace />} />
            <Route path="/es/cotizacion" element={<Navigate to="/es/cotizar" replace />} />
            <Route path="/es/sod-delivery" element={<Navigate to="/es/servicios/entrega-cesped" replace />} />
            <Route path="/es/bulk-delivery" element={<Navigate to="/es/servicios/entrega-granel" replace />} />
            {/* English slugs accidentally linked under /es -> Spanish canonical */}
            <Route path="/es/quote" element={<Navigate to="/es/cotizar" replace />} />
            <Route path="/es/products" element={<Navigate to="/es/productos" replace />} />
            <Route path="/es/grass" element={<Navigate to="/es/pasto" replace />} />
            <Route path="/es/grass/:product" element={<Navigate to="/es/pasto" replace />} />
            <Route path="/es/rock" element={<Navigate to="/es/roca" replace />} />
            <Route path="/es/soil" element={<Navigate to="/es/tierra" replace />} />
            <Route path="/es/sand" element={<Navigate to="/es/arena" replace />} />
            <Route path="/es/services" element={<Navigate to="/es/servicios" replace />} />
            <Route path="/es/services/sod-delivery" element={<Navigate to="/es/servicios/entrega-cesped" replace />} />
            <Route path="/es/services/bulk-delivery" element={<Navigate to="/es/servicios/entrega-granel" replace />} />
            <Route path="/es/contact" element={<Navigate to="/es/contacto" replace />} />
            <Route path="/es/about" element={<Navigate to="/es/nosotros" replace />} />
            <Route path="/es/privacy" element={<Navigate to="/es/privacidad" replace />} />
            <Route path="/es/terms" element={<Navigate to="/es/terminos" replace />} />
            {/* Spanish slugs accidentally linked under /en -> English canonical */}
            <Route path="/en/productos" element={<Navigate to="/en/products" replace />} />
            <Route path="/en/pasto" element={<Navigate to="/en/grass" replace />} />
            <Route path="/en/roca" element={<Navigate to="/en/rock" replace />} />
            <Route path="/en/tierra" element={<Navigate to="/en/soil" replace />} />
            <Route path="/en/arena" element={<Navigate to="/en/sand" replace />} />
            <Route path="/en/servicios" element={<Navigate to="/en/services" replace />} />
            <Route path="/en/contacto" element={<Navigate to="/en/contact" replace />} />
            <Route path="/en/nosotros" element={<Navigate to="/en/about" replace />} />
            <Route path="/en/cotizar" element={<Navigate to="/en/quote" replace />} />
            <Route path="/en/privacidad" element={<Navigate to="/en/privacy" replace />} />
            <Route path="/en/terminos" element={<Navigate to="/en/terms" replace />} />
            <Route path="/grass" element={<Navigate to="/en/grass" replace />} />
            <Route path="/rock" element={<Navigate to="/en/rock" replace />} />
            <Route path="/mulch" element={<Navigate to="/en/mulch" replace />} />
            <Route path="/soil" element={<Navigate to="/en/soil" replace />} />
            <Route path="/sand" element={<Navigate to="/en/sand" replace />} />
            <Route path="/products" element={<Navigate to="/en/products" replace />} />
            <Route path="/services" element={<Navigate to="/en/services" replace />} />
            <Route path="/about" element={<Navigate to="/en/about" replace />} />
            <Route path="/contact" element={<Navigate to="/en/contact" replace />} />
            <Route path="/quote" element={<Navigate to="/en/quote" replace />} />
            <Route path="/blog" element={<Navigate to="/en/blog" replace />} />
            <Route path="/areas" element={<Navigate to="/en/areas" replace />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />

            </Routes>
          </Router>
        </LanguageProvider>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
  );
};

export default App;
